"use client";

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Circle, Edit, Globe, Plus } from "lucide-react";
import { Icon } from "@iconify-icon/react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TemplateSlotDialog } from "@/features/calendar/components/templates/TemplateSlotDialog.tsx";
import { UpdateTemplateDialog } from "@/features/calendar/components/templates/UpdateTemplateDialog.tsx";
import { WorkspaceSwitcher } from "@/features/workspaces/components/widget/workspace-switcher.tsx";
import { SidebarTrigger } from "@/components/ui/sidebar.tsx";
import PATHS from "@/routes/paths.ts";
import { useTemplatesStore } from "@/features/calendar/store/useTemplatesStore";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { getTimezoneAbbreviation } from "@/features/calendar/utils/timezone";

export default function TemplateHeader() {
  const { t } = useTranslation(["common", "calendar", "timezones"]);
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const [isSlotDialogOpen, setIsSlotDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const { currentTemplate, isLoadingTemplate, fetchTemplateDetails } = useTemplatesStore();
  const isMobile = useIsMobile();

  const handleTemplateUpdate = () => {
    if (templateId) {
      fetchTemplateDetails(templateId);
    }
  };

  return (
    <header className="flex h-14 max-h-14 shrink-0 items-center justify-between">
      <div className="mx-2 my-2 flex items-center justify-between">
        {isMobile && <SidebarTrigger />}
        {!isMobile && <WorkspaceSwitcher />}
      </div>
      <div className="tablet:p-4 flex w-full items-center justify-between gap-2">
        <div className="tablet:gap-4 flex w-full min-w-0 items-center">
          <div className="bg-background flex w-full min-w-0 items-center gap-2 sm:flex">
            <Button
              className="h-8 w-8 shrink-0"
              size="icon"
              variant="outline"
              onClick={() => navigate(PATHS.TUTOR.SCHEDULE.TEMPLATES)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>

            {isLoadingTemplate || !currentTemplate ? (
              <div className="flex min-w-0 items-center gap-2">
                <Skeleton className="h-6 w-24 shrink-0" />
                <Skeleton className="h-8 w-48 min-w-0 flex-1" />
              </div>
            ) : (
              <>
                <Badge
                  className={`shrink-0 ${
                    currentTemplate.type === "weekly"
                      ? "bg-muted-foreground/20 text-muted-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {currentTemplate.type === "weekly" ? (
                    <Icon
                      className="sm:mr-1"
                      height={"1rem"}
                      icon="tabler:calendar-week"
                      width={"1rem"}
                    />
                  ) : (
                    <Icon
                      className="sm:mr-1"
                      height={"1rem"}
                      icon="tabler:calendar"
                      width={"1rem"}
                    />
                  )}
                  <span className="max-sm:hidden">
                    {t(`calendar:templates.${currentTemplate.type}`)}
                  </span>
                </Badge>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <h1 className="text-accent-foreground w-fit! max-w-20 min-w-0 truncate text-sm font-bold min-[500px]:max-w-50 sm:max-w-60 sm:text-base md:max-w-100 lg:max-w-140 lg:text-lg">
                      {currentTemplate.name}
                    </h1>
                    <TooltipProvider>
                      <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                          <Button
                            className="bg-muted/20 hover:bg-muted/40 h-8 w-8 shrink-0 p-0 backdrop-blur-sm"
                            size="icon"
                            variant="ghost"
                            onClick={() => setIsRenameDialogOpen(true)}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">{t("common:rename")}</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent align="center" side="right">
                          <p>{t("common:rename")}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                {currentTemplate.timezone && (
                  <Button className="hidden h-9 min-[930px]:flex" variant="ghost">
                    <Globe className="h-4 w-4" />
                    <p className="flex items-center gap-1">
                      {getTimezoneAbbreviation(currentTemplate.timezone)}{" "}
                      <Circle className="h-2! w-2!" fill="#fff" />{" "}
                      {t(
                        // @ts-ignore
                        `timezones:${currentTemplate.timezone}`,
                        {
                          defaultValue: currentTemplate.timezone,
                        },
                      )}
                    </p>
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center">
          <Button
            className="text-xs sm:text-sm"
            disabled={isLoadingTemplate || !currentTemplate}
            size="sm"
            variant="foreground"
            onClick={() => setIsSlotDialogOpen(true)}
          >
            <Plus className="h-3.5 w-3.5 min-[400px]:mr-1" />
            <span className="max-[400px]:hidden">{t("calendar:templates.addSlot")}</span>
          </Button>
        </div>
      </div>

      {/* Add Slot Dialog */}
      {currentTemplate && (
        <TemplateSlotDialog
          isOpen={isSlotDialogOpen}
          templateId={currentTemplate.id}
          templateType={currentTemplate.type}
          onClose={() => setIsSlotDialogOpen(false)}
          onSuccess={handleTemplateUpdate}
        />
      )}

      {/* Rename Template Dialog */}
      {currentTemplate && (
        <UpdateTemplateDialog
          isOpen={isRenameDialogOpen}
          templateId={currentTemplate.id}
          templateName={currentTemplate.name}
          templateTimezone={currentTemplate.timezone}
          templateType={currentTemplate.type}
          onClose={() => setIsRenameDialogOpen(false)}
          onSuccess={handleTemplateUpdate}
        />
      )}
    </header>
  );
}
